import { z } from 'zod'

export const channelSchema = z.enum(['web', 'voice', 'teams', 'kiosk'])
export type Channel = z.infer<typeof channelSchema>

export const localeSchema = z.enum(['de', 'en']).or(z.string().min(2).max(10))
export type Locale = z.infer<typeof localeSchema>

export const consentFlagsSchema = z.object({
  ai: z.boolean(),
  microphone: z.boolean(),
  camera: z.boolean(),
  timestamp: z.string(),
})
export type ConsentFlags = z.infer<typeof consentFlagsSchema>

export const sessionSchema = z.object({
  id: z.string(),
  channel: channelSchema,
  locale: localeSchema,
  addressForm: z.enum(['du', 'sie']).default('du'),
  startedAt: z.string(),
  endedAt: z.string().nullable(),
  visitedShowcaseIds: z.array(z.string()).default([]),
  consent: consentFlagsSchema.nullable(),
  groupMode: z.boolean().default(false),
  /**
   * Jemand aus dem J4F-Team spricht, nicht ein Besucher: der Intro-/Bühnen-Chat
   * (Handy mit Token), Teams, ein Telefonat mit der Rufbereitschaft, die
   * Admin-Testkonsole. Wird **nur vom Server** gesetzt (session-do `init`) —
   * der Web-Einstieg nimmt das Feld nicht an. Schaltet die Werkzeuge frei,
   * die in der Registry `teamOnly` sind (Preise ändern).
   */
  team: z.boolean().default(false),
})
export type Session = z.infer<typeof sessionSchema>

export const messageRoleSchema = z.enum(['user', 'assistant', 'system', 'tool'])
export type MessageRole = z.infer<typeof messageRoleSchema>

export const sourceRefSchema = z.object({
  documentId: z.string(),
  documentTitle: z.string(),
  chunkId: z.string(),
  excerpt: z.string(),
})
export type SourceRef = z.infer<typeof sourceRefSchema>

export const toolCallRecordSchema = z.object({
  id: z.string(),
  name: z.string(),
  input: z.record(z.unknown()),
  output: z.record(z.unknown()).optional(),
  requiresConfirmation: z.boolean().default(false),
  confirmed: z.boolean().optional(),
})
export type ToolCallRecord = z.infer<typeof toolCallRecordSchema>

/**
 * Die jüngste Bestätigung, auf die der Besucher noch nicht geantwortet hat
 * (die Kontaktkarte, ein Ja/Nein) — über den **ganzen** Verlauf, nicht nur
 * die letzte Antwort: Die Karte bleibt offen, wenn er weiterredet.
 *
 * Ein bestätigter oder abgelehnter Aufruf steht zweimal im Verlauf: einmal
 * in der Antwort, die ihn gestellt hat (dort ohne Ergebnis), und einmal in
 * der Antwort auf die Bestätigung (mit). Gilt ein Aufruf irgendwo als
 * entschieden, ist er es — der ältere Eintrag zählt nicht mehr als offen.
 */
export function offeneBestaetigung(
  messages: ReadonlyArray<{ role: string; toolCalls: ToolCallRecord[] }>,
): ToolCallRecord | undefined {
  const alle = messages.flatMap((m) => (m.role === 'assistant' ? m.toolCalls : []))
  const entschieden = new Set(
    alle.filter((c) => c.confirmed !== undefined || c.output !== undefined).map((c) => c.id),
  )
  return alle.findLast(
    (c) => c.requiresConfirmation && c.confirmed === undefined && !entschieden.has(c.id),
  )
}

export const messageSchema = z.object({
  id: z.string(),
  sessionId: z.string(),
  role: messageRoleSchema,
  content: z.string(),
  toolCalls: z.array(toolCallRecordSchema).default([]),
  sources: z.array(sourceRefSchema).default([]),
  /** true when the answer draws on the model's general knowledge, not the KB (FR-05) */
  generalKnowledge: z.boolean().default(false),
  latencyMs: z.number().int().optional(),
  tokensIn: z.number().int().optional(),
  tokensOut: z.number().int().optional(),
  createdAt: z.string(),
})
export type Message = z.infer<typeof messageSchema>
